'use client';

import React from 'react';
import { Box, useTheme } from '@mui/material';

import { useT } from '@/hooks';
import { colors } from '@/styles/theme';
import { EventMarket, EventResponse } from '@/types';
import { capitalizeAllWords, getMarketPriceUpdate, isTennisGame } from '@/utils';
import { MarketButton } from '../MarketButton';

interface BettingGridProps {
  event: EventResponse;
  onTeamClick: (event: EventResponse, teamIndex: 1 | 2, isPreviousMarket?: boolean) => void;
  onPropTradeClick?: (
    marketId: string,
    outcomeId: string,
    isBuy: boolean,
    event?: EventResponse,
  ) => void;
  moneylineMarket?: EventMarket;
  spreadsMarket?: EventMarket;
  totalsMarket?: EventMarket;
  isSpreadActive?: boolean;
  isTotalActive?: boolean;
  team1: { abbr: string; color: string; price: string; id?: string; marketId?: string };
  team2: { abbr: string; color: string; price: string; id?: string; marketId?: string };
  spread1?: { line: number | string; price: string; id?: string; marketId?: string };
  spread2?: { line: number | string; price: string; id?: string; marketId?: string };
  totalOver?: { line: number | string; price: string; id?: string; marketId?: string };
  totalUnder?: { line: number | string; price: string; id?: string; marketId?: string };
  columnVisibility: {
    hasMoneyline: boolean;
    hasSpread: boolean;
    hasTotal: boolean;
    hasDraw?: boolean;
  };
  activeOutcomeId?: string;
  selectedLine?: string | number;
  drawData?: { price: string; id?: string; marketId?: string };
}

export const BettingGrid = ({
  event,
  onTeamClick,
  onPropTradeClick,
  moneylineMarket,
  spreadsMarket,
  totalsMarket,
  isSpreadActive,
  isTotalActive,
  team1,
  team2,
  spread1,
  spread2,
  totalOver,
  totalUnder,
  columnVisibility,
  activeOutcomeId,
  drawData,
}: BettingGridProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { t } = useT();

  if (!event || !team1 || !team2) return null;

  // Matches prod's outcome-button coloring: team-based buttons (moneyline,
  // spread) use each team's own brand color; draw is a neutral slate; totals
  // (over/under) use fixed semantic colors since there's no team to key off.
  const DRAW_COLOR = isDarkMode ? colors.neutral[700] : colors.neutral[200];

  const isTennis = isTennisGame(event);
  const { hasMoneyline, hasSpread, hasTotal, hasDraw } = columnVisibility || {};
  const visibleMarketsCount = [hasMoneyline, hasSpread, hasTotal].filter(Boolean).length;
  const isSingleMarket = visibleMarketsCount === 1 || event.markets?.length === 1;

  const handleTeamClick = (teamIndex: 1 | 2) => (e: React.MouseEvent) => {
    e.stopPropagation();
    onTeamClick(event, teamIndex, false);
  };

  const getPriceUpdate = (market: EventMarket | undefined, index: number) => {
    return getMarketPriceUpdate(market, index);
  };

  const handlePropClick =
    (marketId?: string, outcomeId?: string, isYes: boolean = true) =>
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!marketId || !outcomeId || !onPropTradeClick) return;
      onPropTradeClick(marketId, outcomeId, isYes, event);
    };

  const getMarketClickHandler = (
    marketId: string | undefined,
    outcomeId: string | undefined,
    teamIndex: 1 | 2,
  ) => {
    // If we have distinct market IDs, it's independent. Both selections should result in a "Buy YES" (isYes=true).
    const isIndependent = team1.marketId && team2.marketId && team1.marketId !== team2.marketId;

    if (onPropTradeClick && marketId && outcomeId) {
      return handlePropClick(marketId, outcomeId, isIndependent ? true : teamIndex === 1);
    }

    // If it's a 3-way match (hasDraw), we want each team to be treated as an
    // independent selection (independent YES/NO buttons).
    if (hasDraw) {
      return handleTeamClick(teamIndex);
    }

    return handleTeamClick(teamIndex);
  };

  const highlightMoneyline = !isSpreadActive && !isTotalActive;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: { xs: 1, sm: 1 },
        width: isSingleMarket ? 'auto' : { xs: '100%', md: 'auto' },
        alignItems: 'stretch',
        justifyContent: { xs: 'center', md: 'flex-end' },
        ml: { md: 'auto' },
      }}
    >
      {/* Moneyline */}
      {hasMoneyline && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: hasDraw ? 'row' : isSingleMarket ? 'row' : { xs: 'row', md: 'column' },
            gap: 1,
            width: isSingleMarket ? '100%' : { xs: '100%', md: 'auto' },
            flex: hasDraw ? { xs: 1, md: 'none' } : 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              minWidth: 0,
            }}
          >
            <MarketButton
              label={team1.price ? capitalizeAllWords(team1.abbr) : '---'}
              price={team1.price}
              active={highlightMoneyline}
              disabled={!team1.price}
              color={team1.color}
              onClick={getMarketClickHandler(team1.marketId, team1.id, 1)}
              priceChangeDirection={getPriceUpdate(moneylineMarket, 0)?.direction}
              lastUpdateTimestamp={getPriceUpdate(moneylineMarket, 0)?.timestamp}
            />
          </Box>
          {hasDraw && drawData && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                minWidth: 0,
              }}
            >
              <MarketButton
                label={t('sports.draw', { defaultValue: 'Draw' })}
                price={drawData.price}
                active={highlightMoneyline && activeOutcomeId === drawData.id}
                disabled={!drawData.price}
                color={DRAW_COLOR}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onPropTradeClick && drawData.marketId && drawData.id) {
                    onPropTradeClick(drawData.marketId, drawData.id, true, event);
                  }
                }}
              />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              minWidth: 0,
            }}
          >
            <MarketButton
              label={team2.price ? capitalizeAllWords(team2?.abbr) : '---'}
              price={team2.price}
              active={highlightMoneyline}
              disabled={!team2.price}
              color={team2.color}
              onClick={getMarketClickHandler(team2.marketId, team2.id, 2)}
              priceChangeDirection={getPriceUpdate(moneylineMarket, 1)?.direction}
              lastUpdateTimestamp={getPriceUpdate(moneylineMarket, 1)?.timestamp}
            />
          </Box>
        </Box>
      )}

      {/* Spread */}
      {hasSpread && (
        <Box
          sx={{
            display: { xs: hasMoneyline ? 'none' : 'flex', md: 'flex' },
            flexDirection: isSingleMarket ? 'row' : { xs: 'row', md: 'column' },
            gap: 1,
            width: isSingleMarket ? '100%' : { xs: '100%', md: 'auto' },
            flex: 1,
            minWidth: 120,
          }}
        >
          <MarketButton
            label={spread1?.price ? capitalizeAllWords(team1?.abbr) : '---'}
            price={spread1?.price || ''}
            line={spread1?.line && spread1.line !== '' && spread1.price ? spread1.line : undefined}
            onClick={getMarketClickHandler(spread1?.marketId, spread1?.id, 1)}
            active={isSpreadActive}
            disabled={!spread1?.price}
            color={team1.color}
            priceChangeDirection={getPriceUpdate(spreadsMarket, 0)?.direction}
            lastUpdateTimestamp={getPriceUpdate(spreadsMarket, 0)?.timestamp}
          />
          <MarketButton
            label={spread2?.price ? capitalizeAllWords(team2?.abbr) : '---'}
            price={spread2?.price || ''}
            line={spread2?.line && spread2.line !== '' && spread2.price ? spread2.line : undefined}
            onClick={getMarketClickHandler(spread2?.marketId, spread2?.id, 2)}
            active={isSpreadActive}
            disabled={!spread2?.price}
            color={team2.color}
            priceChangeDirection={getPriceUpdate(spreadsMarket, 1)?.direction}
            lastUpdateTimestamp={getPriceUpdate(spreadsMarket, 1)?.timestamp}
          />
        </Box>
      )}

      {/* Total */}
      {hasTotal && (
        <Box
          sx={{
            display: { xs: hasMoneyline || hasSpread ? 'none' : 'flex', md: 'flex' },
            flexDirection: isSingleMarket ? 'row' : { xs: 'row', md: 'column' },
            gap: 1,
            width: isSingleMarket ? '100%' : { xs: '100%', md: 'auto' },
            flex: 1,
            minWidth: 0,
          }}
        >
          <MarketButton
            label={'O'}
            price={totalOver?.price || ''}
            line={totalOver?.line}
            onClick={getMarketClickHandler(totalOver?.marketId, totalOver?.id, 1)}
            active={isTotalActive}
            disabled={!totalOver?.price}
            color="#359a5e"
            priceChangeDirection={getPriceUpdate(totalsMarket, 0)?.direction}
            lastUpdateTimestamp={getPriceUpdate(totalsMarket, 0)?.timestamp}
          />
          <MarketButton
            label={'U'}
            price={totalUnder?.price || ''}
            line={totalUnder?.line}
            onClick={getMarketClickHandler(totalUnder?.marketId, totalUnder?.id, 2)}
            active={isTotalActive}
            disabled={!totalUnder?.price}
            color="#cb3131"
            priceChangeDirection={getPriceUpdate(totalsMarket, 1)?.direction}
            lastUpdateTimestamp={getPriceUpdate(totalsMarket, 1)?.timestamp}
          />
        </Box>
      )}
    </Box>
  );
};
